/**
 * The signed-in user's own doctor record id, or null if they are not a doctor.
 *
 * Doctor templates belong to the signed-in doctor (the backend resolves the
 * owner from the token), so template features on a visit are only offered
 * when this id is the visit's doctor. Cached per user so it resolves offline.
 * `undefined` while unknown (first load, never resolved online).
 */
import { useEffect, useState } from "react";
import { AUTH_USER_ID_KEY } from "../components/RequireAuth";
import { OnboardingAPI } from "../services/onboarding.service";

const cacheKey = (userId: string) => `kgz_own_doctor_id:${userId}`;

function currentUserId(): string | null {
  try { return localStorage.getItem(AUTH_USER_ID_KEY); } catch { return null; }
}

function readCached(): string | null | undefined {
  const uid = currentUserId();
  if (!uid) return undefined;
  try {
    const raw = localStorage.getItem(cacheKey(uid));
    return raw === null ? undefined : raw === "" ? null : raw;
  } catch {
    return undefined;
  }
}

/**
 * True when `doctorRef` is the signed-in user. Visit URLs carry the doctor's
 * record id, or for self-enrolled doctors their user id, so both are checked.
 */
export function useIsSignedInDoctor(doctorRef: string | null | undefined): boolean {
  const own = useSignedInDoctorId();
  if (!doctorRef) return false;
  return doctorRef === own || doctorRef === currentUserId();
}

export function useSignedInDoctorId(): string | null | undefined {
  const [id, setId] = useState<string | null | undefined>(readCached);
  useEffect(() => {
    let cancelled = false;
    OnboardingAPI.getMe()
      .then((me) => {
        const own = me.doctor_id ?? null;
        const uid = currentUserId();
        if (uid) { try { localStorage.setItem(cacheKey(uid), own ?? ""); } catch { /* non-fatal */ } }
        if (!cancelled) setId(own);
      })
      .catch(() => { /* offline: keep the cached value */ });
    return () => { cancelled = true; };
  }, []);
  return id;
}
